import { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import FileText from '/assets/sources/filetext.svg';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { storageService } from '@/services';
import { useCreateSource } from '@/hooks/use-sources';
import type { CreateSourceDto } from '@/types';
import { MarkAsSopToggle } from './mark-as-sop-toggle';
import { FOR_PROTOCOL_NOTE } from '../../constants';

const ALLOWED_FILE_TYPES = ['.pdf', '.docx', '.doc', '.txt', '.md'];

function isMarkdown(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.md');
}

interface PendingFile {
  file: File;
  markAsSop: boolean;
}

interface AddDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded?: () => void;
}

export function AddDocumentsDialog({ open, onOpenChange, onAdded }: AddDocumentsDialogProps) {
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createSourceMutation = useCreateSource();

  const reset = () => {
    setPending([]);
    setIsDragging(false);
    setIsSubmitting(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const valid: File[] = [];
    const invalid: File[] = [];
    Array.from(files).forEach((file) => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      (ALLOWED_FILE_TYPES.includes(extension) ? valid : invalid).push(file);
    });
    if (invalid.length > 0) {
      toast.error(`Unsupported file format: ${invalid.map((f) => f.name).join(', ')}`);
    }
    if (valid.length > 0) {
      // Only markdown uploads can become a protocol draft — PDFs/DOCX/TXT stay plain Knowledge sources.
      setPending((prev) => [...prev, ...valid.map((file) => ({ file, markAsSop: isMarkdown(file.name) }))]);
    }
  };

  const removeFile = (index: number) => {
    setPending((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleMarkAsSop = (index: number, checked: boolean) => {
    setPending((prev) => prev.map((p, i) => (i === index ? { ...p, markAsSop: checked } : p)));
  };

  const handleSubmit = async () => {
    if (pending.length === 0) return;
    setIsSubmitting(true);
    try {
      for (const { file, markAsSop } of pending) {
        const uploadUrlData = await storageService.generateUploadUrl(file.name, file.type, { sourceType: 'file' });
        await storageService.uploadFile(uploadUrlData.url, file);
        const sourceData: CreateSourceDto = {
          name: file.name,
          type: 'FILE',
          value: uploadUrlData.key,
          metadata: {
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
            key: uploadUrlData.key,
            markAsSop
          }
        };
        await createSourceMutation.mutateAsync(sourceData);
      }
      const forProtocol = pending.filter((p) => p.markAsSop).length;
      toast.success(`Successfully added ${pending.length} document(s)`, {
        ...(forProtocol > 0 && { description: `${forProtocol} for a protocol. ${FOR_PROTOCOL_NOTE}` })
      });
      onAdded?.();
      handleOpenChange(false);
    } catch (error) {
      toast.error('Failed to add documents');
      console.error('Add documents error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] max-w-lg p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Add Documents</DialogTitle>
        </DialogHeader>

        <div
          className={cn(
            'h-[180px] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-3.5 transition-colors cursor-pointer',
            isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <img src={FileText} alt='' className='size-8' />
          <div className='text-center'>
            <p className='font-semibold text-sm'>Upload Documents</p>
            <p className='text-xs text-muted-foreground'>Allowed file types are .pdf, .docx, .txt, .md</p>
          </div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='text-xs border-gray-300'
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            <Upload className='size-4' /> Upload
          </Button>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={(e) => {
              handleFiles(e.target.files);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className='hidden'
          />
        </div>

        {pending.length > 0 && (
          <div className='flex flex-col gap-3 max-h-[220px] overflow-y-auto'>
            {pending.map((p, index) => (
              <div key={`${p.file.name}-${index}`} className='flex flex-col gap-3 rounded-lg border p-3'>
                <div className='flex items-center justify-between gap-2'>
                  <div className='min-w-0'>
                    <p className='text-xs font-medium truncate'>{p.file.name}</p>
                    <p className='text-[11px] text-muted-foreground'>{(p.file.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                  <Button variant='ghost' size='icon-sm' onClick={() => removeFile(index)}>
                    <X className='size-4' />
                  </Button>
                </div>
                {isMarkdown(p.file.name) && (
                  <MarkAsSopToggle checked={p.markAsSop} onCheckedChange={(checked) => toggleMarkAsSop(index, checked)} />
                )}
              </div>
            ))}
          </div>
        )}

        <div className='flex gap-2 justify-end mt-2'>
          <Button type='button' variant='outline' className='text-xs' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type='button' className='text-xs' onClick={handleSubmit} disabled={pending.length === 0 || isSubmitting}>
            {isSubmitting ? 'Adding…' : 'Add Documents'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
